import { twJoin } from 'tailwind-merge';

import { ButtonConfigProps } from 'molecules/Button/config';
import buttonVariants from 'molecules/Button/styles';
import Icon from 'molecules/Icon';

import { analyticsClient } from 'utils/analytics/client';
import { trackBuilderConversion } from 'utils/builder/conversion';
import parseUrl from 'utils/parse/parseUrl';
import { scrollToAnchor } from 'utils/scroll/scrollToAnchor';

import type { VariantProps } from 'class-variance-authority';
import type { CSSProperties, ElementType, FC, ReactNode } from 'react';

export interface ButtonProps extends MergePrefer<VariantProps<typeof buttonVariants>, ButtonConfigProps> {
  /**
   * The children prop represents the content that will be rendered inside the Button component.
   */
  children?: ReactNode;
  /**
   * The `className` prop allows you to add additional CSS classes to the Button component.
   * This can be used to apply custom styles or override the default styles of the Button.
   */
  className?: string;
  /**
   * The `semanticFallback` prop allows you to specify an alternative HTML element to use for the Button component.
   * This can be useful if you need to use a different semantic element (e.g. `<a>` instead of `<button>`) for accessibility or other reasons.
   * The value should be a valid HTML element type, such as 'a', 'div', or 'span'.
   */
  semanticFallback?: ElementType;
  /**
   * The `disabled` prop represents whether the Button component should be disabled and not interactive.
   * When set to `true`, the Button will be rendered in a disabled state and will not respond to user interactions.
   */
  disabled?: boolean;
  /**
   * The `onClick` prop represents a function that will be called when the Button component is clicked.
   * The function will receive the `React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>` object as its argument, which can be used to access information about the click event.
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;

  /** NEW: when true we read color fields and feed CSS vars */
  useCustomColors?: boolean;
  useGradient?: boolean;
  bgColor?: string;
  hoverBgColor?: string;
  gradientFrom?: string;
  gradientTo?: string;
  hoverGradientFrom?: string;
  hoverGradientTo?: string;
  textColor?: string;
  borderColor?: string;
  focusRingColor?: string;
  size?: number;
}

type ButtonCSSVars = CSSProperties & {
  '--btn-text'?: string;
  '--btn-border'?: string;
  '--btn-focus'?: string;
  '--btn-bg'?: string;
  '--btn-bg-hover'?: string;
  '--btn-from'?: string;
  '--btn-to'?: string;
  '--btn-hfrom'?: string;
  '--btn-hto'?: string;
};

interface ButtonIconProps {
  /**
   * The `icon` property represents an optional icon to display in the Button component.
   * The value should be one of the `ButtonIds` enum values, which define the available icons.
   */
  icon: ButtonProps['startIcon'];
  size?: number;
}

const ButtonIcon: FC<ButtonIconProps> = ({ icon, size = 20 }) =>
  icon &&
  icon !== 'No Icon' && (
    <Icon icon={icon} className="z-10 scale-75 stroke-[1.5] icon-current sm:scale-100" size={size} />
  );

// Lighthouse SEO/accessibility flags links whose accessible name is a generic
// phrase like "Learn more". Builder lets marketing set short CTA labels, so
// when the visible label is one of these phrases we synthesize a more
// descriptive aria-label from the destination URL (e.g. "Learn more about
// platform" for href="/platform/"). The visible text stays unchanged.
const GENERIC_LINK_PHRASES = new Set([
  'learn more',
  'read more',
  'see more',
  'view more',
  'click here',
  'more',
  'details',
]);

// Lighthouse SEO's link-text audit reads visible textContent, not aria-label,
// so we inject a `sr-only` span with the descriptive suffix when the label is
// generic. The visible label stays unchanged, but crawlers + screen readers
// see "Learn more about platform" instead of bare "Learn more".
const descriptiveSuffix = (label: string | undefined, href: string | undefined): string => {
  if (!label || !href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
    return '';
  }
  const trimmed = label.trim();
  const isGeneric = GENERIC_LINK_PHRASES.has(trimmed.toLowerCase());
  if (!isGeneric) {
    return '';
  }
  try {
    const path = href.startsWith('http') ? new URL(href).pathname : href;
    const segments = path.split('/').filter(p => p && !p.includes('.'));
    if (!segments.length) {
      return '';
    }
    const last = segments[segments.length - 1];
    const readable = last.replace(/[-_]+/g, ' ');

    return ` about ${readable}`;
  } catch {
    return '';
  }
};

const buildAccessibleName = (label: string | undefined, href: string | undefined): string => {
  if (!label) {
    return 'button';
  }
  const trimmed = label.trim();
  const suffix = descriptiveSuffix(label, href);

  return suffix ? `${trimmed}${suffix}` : trimmed;
};

const Button: FC<ButtonProps> = ({
  children,
  hierarchy,
  href,
  mobileHref,
  startIcon,
  endIcon,
  label,
  className,
  semanticFallback,
  forceSmallerSize,
  onClick,
  useCustomColors,
  useGradient,
  bgColor,
  hoverBgColor,
  gradientFrom,
  gradientTo,
  hoverGradientFrom,
  hoverGradientTo,
  textColor,
  borderColor,
  focusRingColor,
  size = 20,
  ...props
}) => {
  const { as: Component, ...urlProps } = parseUrl(href, semanticFallback || 'button');

  const handleClick = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    const useMobileHref =
      !!mobileHref && typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches;
    const targetHref = useMobileHref ? mobileHref : href;

    if (useMobileHref) {
      e.preventDefault();
      window.location.href = mobileHref!;
    } else if (href?.startsWith('#')) {
      e.preventDefault();
      scrollToAnchor(href);
    }

    analyticsClient.track('Clicked CTA', {
      name: label,
      external: targetHref,
      segmentType: 'appPage',
    });
    trackBuilderConversion();
    onClick?.(e);
  };

  const customVars: ButtonCSSVars | undefined = useCustomColors
    ? {
        ...(textColor ? { '--btn-text': textColor } : {}),
        ...(borderColor ? { '--btn-border': borderColor } : {}),
        ...(focusRingColor ? { '--btn-focus': focusRingColor } : {}),
        ...(useGradient
          ? {
              ...(gradientFrom ? { '--btn-from': gradientFrom } : {}),
              ...(gradientTo ? { '--btn-to': gradientTo } : {}),
              ...(hoverGradientFrom ? { '--btn-hfrom': hoverGradientFrom } : {}),
              ...(hoverGradientTo ? { '--btn-hto': hoverGradientTo } : {}),
            }
          : {
              ...(bgColor ? { '--btn-bg': bgColor } : {}),
              ...(hoverBgColor ? { '--btn-bg-hover': hoverBgColor } : {}),
            }),
      }
    : undefined;

  let customModeClasses = '';
  if (useCustomColors) {
    if (useGradient) {
      customModeClasses =
        'bg-[linear-gradient(180deg,var(--btn-from)_0%,var(--btn-to)_100%)] hover:bg-[linear-gradient(180deg,var(--btn-hfrom)_0%,var(--btn-hto)_100%)]';
    } else {
      customModeClasses = 'bg-[var(--btn-bg)] hover:bg-[var(--btn-bg-hover)]';
    }
  }

  const effectiveHierarchy = useCustomColors ? ('custom' as const) : hierarchy;
  const srSuffix = descriptiveSuffix(label, href);
  // Only override aria-label when we have a meaningful one to compute from
  // `label`. Icon-only buttons (no `label`) rely on caller-provided aria-label,
  // so leave that untouched and let the props spread carry it through.
  const ariaLabelOverride = label ? { 'aria-label': buildAccessibleName(label, href) } : {};

  return (
    <Component
      className={twJoin(
        buttonVariants({ hierarchy: effectiveHierarchy, forceSmallerSize: !!forceSmallerSize }),
        customModeClasses,
        className,
      )}
      style={customVars}
      onClick={handleClick}
      {...urlProps}
      {...props}
      {...ariaLabelOverride}
    >
      {startIcon && <ButtonIcon icon={startIcon} />}
      {label || children}
      {srSuffix && <span className="sr-only">{srSuffix}</span>}
      {endIcon && <ButtonIcon icon={endIcon} size={size} />}
    </Component>
  );
};

export default Button;
